import { motion, useInView } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import ScrollAnimation from '../animations/ScrollAnimation'
import { getResume } from '../../services/api'
import type { Resume } from '../../types/database'

const transition = { duration: 0.6, ease: [0.25, 1, 0.5, 1] }
const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function Resume() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [resume, setResume] = useState<Resume | null>(null)

  useEffect(() => {
    const loadResume = async () => {
      const data = await getResume()
      setResume(data)
    }
    loadResume()
  }, [])


  return (
    <section
      id="resume"
      ref={ref}
      className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-background print:py-8"
    >
      <div className="container mx-auto max-w-4xl relative z-10 print:max-w-full">
        {resume && resume.name && (
          <div className="hidden print:block mb-8">
            <h1 className="text-4xl font-bold mb-2">{resume.name}</h1>
            <p className="text-xl text-text-primary/70 mb-4">{resume.title}</p>
            <div className="flex gap-4 text-sm text-text-primary/70">
              <span>{resume.email}</span>
              <span>•</span>
              <span>{resume.phone}</span>
              <span>•</span>
              <span>{resume.location}</span>
            </div>
          </div>
        )}


        <ScrollAnimation variant="slideUp" delay={0.2} className="text-center mb-16 print:hidden">
          <motion.span
            className="inline-block px-4 py-2 text-xs font-medium text-accent mb-6 uppercase tracking-widest"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
          >
            Resume
          </motion.span>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold mb-6 font-display leading-[1.1] tracking-tight">
            <span className="text-gradient-glow">My</span>{' '}
            <span className="text-text-primary">Experience</span>
          </h2>
        </ScrollAnimation>

        <div className="space-y-8">
          {/* PDF Download Section */}
          {resume?.pdf_url && resume.pdf_url.trim() !== '' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...transition, delay: 0.3 }}
              className="neu p-8 print:shadow-none print:border-none"
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-semibold text-text-primary mb-2">Download Resume</h3>
                  <p className="text-text-primary/70 text-sm">Get a PDF copy of my resume</p>
                </div>
                <div className="flex items-center gap-3">
                  <motion.a
                    href={resume.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 neu rounded-xl text-sm font-medium text-accent hover:text-accent-secondary transition-colors neu-hover"
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                  >
                    Open in New Tab →
                  </motion.a>
                  <motion.a
                    href={resume.pdf_url}
                    download
                    className="px-6 py-3 bg-accent text-white rounded-xl text-sm font-medium hover:bg-accent/90 transition-colors neu-hover"
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                  >
                    Download PDF
                  </motion.a>
                </div>
              </div>
            </motion.div>
          )}

          {/* Header */}
          {resume && resume.name ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...transition, delay: resume.pdf_url ? 0.4 : 0.3 }}
              className="neu p-8 print:shadow-none print:border-none"
            >
              <div className="text-center mb-8 pb-8 border-b border-text-primary/10">
                <h1 className="text-5xl sm:text-6xl font-bold mb-3 text-text-primary">{resume.name}</h1>
                <p className="text-2xl sm:text-3xl text-accent font-medium mb-6">{resume.title}</p>
                <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-text-primary/70">
                  <a href={`mailto:${resume.email}`} className="hover:text-accent transition-colors">
                    {resume.email}
                  </a>
                  <span className="text-text-primary/30">•</span>
                  <a href={`tel:${resume.phone}`} className="hover:text-accent transition-colors">
                    {resume.phone}
                  </a>
                  <span className="text-text-primary/30">•</span>
                  <span>{resume.location}</span>
                </div>
              </div>
              {resume.summary && (
                <div className="mt-6">
                  <h2 className="text-xl font-semibold text-text-primary mb-3">Summary</h2>
                  <p className="text-text-primary/70 leading-relaxed">{resume.summary}</p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...transition, delay: 0.3 }}
              className="neu p-8 print:shadow-none print:border-none text-center"
            >
              <p className="text-text-primary/70">Resume information will be displayed here once it's added.</p>
            </motion.div>
          )}

          {/* Experience */}
          {resume && (resume.experience || []).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...transition, delay: resume.pdf_url ? 0.5 : 0.4 }}
              className="neu p-8 print:shadow-none print:border-none"
            >
              <h3 className="text-3xl font-semibold mb-8 text-text-primary border-b border-text-primary/10 pb-4">Experience</h3>
              <div className="space-y-8">
                {(resume.experience || []).map((exp, index) => (
                  <div key={exp.id} className={index > 0 ? 'pt-8 border-t border-text-primary/10' : ''}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4">
                      <div className="flex-1">
                        <h4 className="text-xl font-semibold text-text-primary mb-1">{exp.title}</h4>
                        <p className="text-accent font-medium text-lg">{exp.company}</p>
                      </div>
                      <span className="text-sm font-medium text-text-primary/70 whitespace-nowrap">{exp.years}</span>
                    </div>
                    {exp.description && (
                      <div className="mb-4">
                        {exp.description.split('\n').map((line, idx) => (
                          <p key={idx} className="text-text-primary/70 leading-relaxed mb-2">
                            {line.trim() && '• '}
                            {line.trim() || '\u00A0'}
                          </p>
                        ))}
                      </div>
                    )}
                    {exp.skills && exp.skills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {exp.skills.map((skill, idx) => (
                          <span key={idx} className="px-3 py-1 bg-accent/10 text-accent rounded-lg text-xs font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Education */}
          {resume && (resume.education || []).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...transition, delay: resume.pdf_url ? 0.6 : 0.5 }}
              className="neu p-8 print:shadow-none print:border-none"
            >
              <h3 className="text-3xl font-semibold mb-8 text-text-primary border-b border-text-primary/10 pb-4">Education</h3>
              <div className="space-y-6">
                {(resume.education || []).map((edu, index) => (
                  <div key={edu.id} className={index > 0 ? 'pt-6 border-t border-text-primary/10' : ''}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                      <div className="flex-1">
                        <h4 className="text-xl font-semibold text-text-primary mb-1">{edu.degree}</h4>
                        <p className="text-accent font-medium text-lg">{edu.university}</p>
                      </div>
                      <span className="text-sm font-medium text-text-primary/70 whitespace-nowrap">{edu.years}</span>
                    </div>
                    {edu.details && (
                      <p className="text-sm text-text-primary/70 mt-3 leading-relaxed">{edu.details}</p>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Skills */}
          {resume && (resume.skills || []).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...transition, delay: resume.pdf_url ? 0.7 : 0.6 }}
              className="neu p-8 print:shadow-none print:border-none"
            >
              <h3 className="text-3xl font-semibold mb-8 text-text-primary border-b border-text-primary/10 pb-4">Skills</h3>
              <div className="flex flex-wrap gap-3">
                {(resume.skills || []).map((skill, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: (resume.pdf_url ? 0.7 : 0.6) + index * 0.03 }}
                    className="px-4 py-2 bg-accent/10 text-accent rounded-lg text-sm font-medium neu-hover"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  )
}


